import { useQuery } from '@tanstack/react-query'
import { useRef, useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { buildApiUrl, buildImageUrl } from '../config'
import { useAppStore } from '../stores/appStore'
import HierarchicalLegSelector from './HierarchicalLegSelector'
import React from 'react'
import { filterToppersForAdjustableBed, filterToppersForWidth, selectBestTopperModel } from '../utils/topperFiltering'

interface ThreeDTexture {
  id: number
  name: string
  base_color_url?: string | null
  normal_url?: string | null
  roughness_url?: string | null
  metalness_url?: string | null
  ambient_occlusion_url?: string | null
}

interface MaterialTexture {
  id: number
  order?: number
  slot_name: string
  slot_label: string
  texture: ThreeDTexture
  textile_repeat_multiplier?: number
}

interface Option {
  id: string
  title: string
  slug: string
  product_thumbnail?: string
  order: number
  on_floor?: boolean
  textile_id?: string
  stitch_color?: string | null
  textures?: MaterialTexture[]
  headboard_textile_for?: {
    id: number
    title: string
    slug: string
    stitch_color?: string | null
    textures: MaterialTexture[]
  } | null
  three_d_model?: {
    id: number
    file_url: string
    preview_image_url?: string | null
  }
  three_d_models?: Array<{
    width_cm: number | null
    is_split: boolean
    three_d_model: {
      id: number
      file_url: string
      preview_url?: string | null
    }
  }>
  texture_options?: Array<{
    slot_name: string
    slot_label: string
    textures: Array<{
      id: number
      name: string
      base_color_url?: string | null
      normal_url?: string | null
      roughness_url?: string | null
      metallic_url?: string | null
      ao_url?: string | null
    }>
  }>
  // Add more fields as needed based on your API response
}

interface OptionsFetcherProps {
  bedId: string
  optionType: string // e.g., 'textile', 'legs', 'headboard', etc.
}

export default function OptionsFetcher({ bedId, optionType }: OptionsFetcherProps) {
  // Select only the store slices we actually need to avoid rerenders on unrelated state (e.g., preferSeamless)
  const market = useAppStore((s) => s.market)
  const lang = useAppStore((s) => s.lang)
  const bedConfig = useAppStore((s) => s.bedConfig)
  const setSelectedTextile = useAppStore((s) => s.setSelectedTextile)
  const setBedComponent = useAppStore((s) => s.setBedComponent)
  const { t } = useTranslation()
  // Hooks must be called unconditionally and in consistent order
  const selectedSize = useAppStore((s) => s.bedConfig.bed.selectedSize)
  const variationModels = useAppStore((s) => s.bedConfig.bed.variation?.models || []) as any[]
  const hasSeamlessForSelectedSize = useMemo(() => {
    if (!selectedSize) return false
    return variationModels.some((m: any) => m.width_cm === selectedSize && m.seamless === true)
  }, [selectedSize, variationModels])
  
  const { data: response, isLoading, error } = useQuery({
    queryKey: ['options', bedId, optionType, market, lang, optionType === 'headboards' ? selectedSize : null],
    queryFn: async () => {
      // Map option types to their specific endpoints
      const endpointMap: Record<string, string> = {
        'textiles': 'materials',
        'toppers': 'toppers',
        'legs': 'legs',
        'headboards': 'headboards',
        'bedside-tables': 'bedsidetables',
        'ottomans': 'ottomans',
        'lamps': 'lamps'
      }
      
      const endpoint = endpointMap[optionType] || 'materials'

      // Use bed width when fetching headboards so API only returns width-matching options
      const basePath = `bed-variations/${bedId}/${endpoint}/`
      const params = new URLSearchParams({
        market,
        lang,
      })
      if (optionType === 'headboards' && selectedSize) {
        params.set('width_cm', String(selectedSize))
      }
      const url = buildApiUrl(`${basePath}?${params.toString()}`)
      
      // Debug logging
      if (!bedId || !market || !lang) {
        console.error('[OptionsFetcher] Missing required params:', { bedId, market, lang, optionType })
      }
      
      const response = await fetch(url)
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error')
        console.error(`[OptionsFetcher] Failed to fetch ${optionType}:`, response.status, errorText, { url, bedId, market, lang })
        throw new Error(`Failed to fetch ${optionType}: ${response.status} ${errorText}`)
      }
      const data = await response.json()
      
      // Debug logging for empty results
      if (!data?.options || data.options.length === 0) {
        console.warn(`[OptionsFetcher] Empty options returned for ${optionType}:`, { url, bedId, market, lang, data })
      }
      console.log(data)
      // Log materials/textiles missing textures
      if (optionType === 'textiles' && data?.missing_textures && data.missing_textures.length > 0) {
        console.warn(
          `[OptionsFetcher] Bed variation "${bedId}" has ${data.missing_textures.length} material(s) without textures:`,
          data.missing_textures.map((m: Option) => ({
            id: m.id,
            title: m.title,
            slug: m.slug
          }))
        )
      }
      
      return data as {
        bed_variation: any
        options: Option[]
        count: number
        missing_textures?: Option[]
      }
    },
    enabled: !!bedId && !!market && !!lang,
  })

  let options = response?.options || []
  
  // Filter toppers for adjustable beds: only show toppers that have reclined models
  const bedVariation = useAppStore.getState().bedConfig.bed.variation
  const isAdjustable = bedVariation?.bed_type_value === 'adjustable'
  const selectedWidth = useAppStore.getState().bedConfig.bed.selectedSize
  
  if (optionType === 'toppers' && isAdjustable && selectedWidth) {
    options = filterToppersForAdjustableBed(options, selectedWidth)
  }
  if (optionType === 'toppers' && selectedWidth) options = filterToppersForWidth(options, selectedWidth)

  // For headboards: only show options that actually have a model for the current bed width
  if (optionType === 'headboards' && selectedWidth) {
    options = options.filter((opt: any) => {
      const models = Array.isArray(opt.three_d_models) ? opt.three_d_models : []
      return models.some((m: any) => m?.width_cm === selectedWidth && m?.three_d_model?.file_url)
    })
  }
  
  // Clear the selected topper if it is no longer available for the current bed width.
  useEffect(() => {
    if (optionType === 'toppers' && response?.options) {
      const bedWidth = useAppStore.getState().bedConfig.bed.selectedSize
      if (bedWidth && options.length >= 0) {
        const currentTopper = useAppStore.getState().bedConfig.topper
        if (currentTopper) {
          const currentTopperId = currentTopper.id || currentTopper.id?.toString()
          const isCurrentTopperInFiltered = options.some((t: any) => 
            (t.id === currentTopperId) || (t.id?.toString() === currentTopperId?.toString())
          )
          
          if (!isCurrentTopperInFiltered) {
            useAppStore.getState().setBedComponent('topper', null)
          }
        }
      }
    }
  }, [optionType, options, response?.options])

  const inferLegMaterialType = (leg: any): string => {
    const title: string = (leg?.title || '').toLowerCase()
    const slug: string = (leg?.slug || '').toLowerCase()
    if (title.includes('metal') || slug.includes('metal') || title.includes('steel') || slug.includes('steel')) return 'metal'
    if (title.includes('oak') || title.includes('walnut') || title.includes('wood') || slug.includes('wood')) return 'wood'
    return 'wood'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading {optionType}...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">
        Error loading options: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    )
  }

  if (options.length === 0 && optionType !== 'legs') {
    return (
      <div className="p-4 text-gray-500">
        {t('optionsFetcher.noOptionAvailable', {
          optionType: t(`optionsFetcher.optionType.${optionType}`, optionType)
        })}
      </div>
    )
  }

  const isTextiles = optionType === 'textiles'
  const isLegs = optionType === 'legs'
  const isHeadboards = optionType === 'headboards'
  const isToppers = optionType === 'toppers'
  const isBedsideTables = optionType === 'bedside-tables'
  const isOttomans = optionType === 'ottomans'


  return (
    <>
      {/* Use hierarchical selector for legs */}
      {isLegs ? (
        <HierarchicalLegSelector bedId={bedId} />
      ) : (
        <>
          {/* Seamless toggle (outside scroll container so label is always visible) */}
          {isTextiles && hasSeamlessForSelectedSize && (
            <div className="px-6"><InlineSeamlessToggle /></div>
          )}
          <div className="px-6">
            <div className="relative">
              {/* JS scroll controls overlay (outside scroller so it doesn't scroll away) */}
              <ScrollControls fullHeight paddingBottom />
              <div className="w-full overflow-x-auto snap-x snap-mandatory scroll-buttons">
                <div className="flex gap-2 w-max">
            {options.map((option) => {
              console.log(option)
              const isSelected = isTextiles
                ? bedConfig.selectedTextileId === option.id
                : isHeadboards
                  ? (bedConfig.headboard as any)?.id === option.id
                  : isToppers
                    ? (bedConfig.topper as any)?.id === option.id
                    : isBedsideTables
                      ? (bedConfig.bedsideTable as any)?.id === option.id
                      : isOttomans
                        ? (bedConfig.ottoman as any)?.id === option.id
                        : false
              return (
                <div
                data-id={option.id}
                data-type={optionType}
                key={option.id}
                className={`snap-center cursor-pointer`}
                onClick={() => {
                  if (isTextiles && option.textures) {
                    // Log slot information when textile has multiple textures
                    if (option.textures.length > 1) {
                      const textileSlots = option.textures.map((t: any, index: number) => ({
                        index,
                        slot_name: t.slot_name || 'default',
                        slot_label: t.slot_label || t.slot_name || 'default',
                        texture_id: t.texture?.id,
                      }))
                      
                      // Get material slots from the actual 3D model
                      const modelMaterialSlots = (window as any).__bed_model_material_slots__ || []
                      
                      console.log('[MultiTextileDebug] Textile selected with multiple textures', {
                        textileId: option.id,
                        textileSlug: option.slug,
                        textileTitle: option.title,
                        textileSlots,
                        modelMaterialSlots,
                      })
                    }
                    
                    // pick first texture as current (can change later)
                    const first = option.textures[0]?.texture || null
                    
                    setSelectedTextile(option.id)
                    
                    // attach selected textile onto bed config (no thumbnail, only identity)
                    setBedComponent('textile', {
                      id: (option as any).id as any,
                      name: option.title,
                      slug: option.slug,
                      textures: option.textures,
                      currentTexture: first,
                      stitch_color: (option as any).stitch_color,
                      headboard_textile_for: option.headboard_textile_for ? {
                        id: option.headboard_textile_for.id,
                        name: option.headboard_textile_for.title,
                        slug: option.headboard_textile_for.slug,
                        stitch_color: option.headboard_textile_for.stitch_color || null,
                        textures: option.headboard_textile_for.textures || [],
                        currentTexture: option.headboard_textile_for.textures?.[0]?.texture || null,
                      } : null,
                    } as any)
                  }
                  if (isHeadboards) {
                    const headboard = option as any
                    const selectedWidth = useAppStore.getState().bedConfig.bed.selectedSize
                    const models = (headboard.three_d_models || []) as Array<{
                      width_cm: number | null
                      three_d_model: { id: number, file_url: string, preview_url?: string | null }
                    }>
                    const chosen = selectedWidth
                      ? models.find(m => m.width_cm === selectedWidth)?.three_d_model || null
                      : null

                    if (!chosen || !chosen.file_url) {
                      console.warn('[OptionsFetcher] No headboard model matching current width', {
                        headboardId: headboard.id,
                        selectedWidth,
                      })
                      return
                    }

                    setBedComponent('headboard', {
                      id: headboard.id,
                      name: headboard.title,
                      slug: headboard.slug,
                      three_d_model: chosen,
                      on_floor: !!headboard.on_floor,
                      offsets: headboard.offsets || { x: 0, y: 0, z: 0 },
                      texture_options: headboard.texture_options || [],
                      currentTexture: ((headboard.texture_options || [])[0]?.textures || [])[0] || null,
                    } as any)
                  }
              if (isToppers) {
                const topper = option as any
                const selectedWidth = useAppStore.getState().bedConfig.bed.selectedSize
                const bedVariation = useAppStore.getState().bedConfig.bed.variation
                const isAdjustable = bedVariation?.bed_type_value === 'adjustable'
                const preferSeamless = useAppStore.getState().preferSeamless
                // For adjustable beds, we might need is_split, but for now use preferSeamless as a proxy
                // Actually, is_split is for split-top toppers on adjustable beds, not seamless
                // We'll need to check if bed is adjustable and pass is_split accordingly
                const models = (topper.three_d_models || []) as Array<{ 
                  width_cm: number | null
                  is_split: boolean
                  reclined_side?: string | null
                  three_d_model: { id: number, file_url: string, preview_url?: string | null } 
                }>
                
                // Find best matching model using reusable function
                const chosen = selectBestTopperModel(
                  models,
                  selectedWidth || 0,
                  isAdjustable,
                  topper.three_d_model || null
                )
                
                setBedComponent('topper', {
                  id: topper.id,
                  name: topper.title,
                  slug: topper.slug,
                  offsets: topper.offsets || { x: 0, y: 0, z: 0 },
                  three_d_model: chosen,
                  three_d_models: models, // Store full array for reclined model lookup
                  texture_options: (topper.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: ThreeDTexture[] }>,
                  currentTexture: ((topper.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: ThreeDTexture[] }>)[0]?.textures?.[0] || null,
                } as any)
              }
              if (isBedsideTables) {
                const bst = option as any
                // Toggle behavior: clicking the currently selected table deselects it
                if ((bedConfig.bedsideTable as any)?.id === bst.id) {
                  setBedComponent('bedsideTable', null as any)
                  return
                }
                if (bst.three_d_model) {
                  const slots = (bst.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: ThreeDTexture[] }>
                  const firstSlot = slots[0]
                  const firstTexture = firstSlot?.textures?.[0] || null
                  setBedComponent('bedsideTable', {
                    id: bst.id,
                    name: bst.title,
                    slug: bst.slug,
                    on_floor: (bst as any).on_floor,
                    offsets: (bst as any).offsets,
                    three_d_model: bst.three_d_model,
                    texture_options: slots,
                    currentTexture: firstTexture,
                  } as any)
                }
              }
              if (isOttomans && (option as any).three_d_model) {
                const ottoman = option as any
                if ((bedConfig.ottoman as any)?.id === ottoman.id) {
                  setBedComponent('ottoman', null as any)
                  return
                }
                const ottomanName = ottoman.title || ''
                const isSaturn = ottomanName.toLowerCase() === 'saturn'
                const slots = (ottoman.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: ThreeDTexture[] }>
                // Saturn uses bed textile (will be handled in Scene), others use their own texture
                const firstTexture = isSaturn ? null : (slots[0]?.textures?.[0] || null)
                setBedComponent('ottoman', {
                  id: ottoman.id,
                  name: ottoman.title,
                  slug: ottoman.slug,
                  three_d_model: ottoman.three_d_model,
                  texture_options: slots,
                  currentTexture: firstTexture,
                } as any)
              }
            }}
          >
            {option.product_thumbnail && (
              <img
                src={buildImageUrl(option.product_thumbnail)}
                alt={option.title}
                className={`aspect-square w-36 mb-2 ${isSelected ? 'border-2 border-white outline outline-1 outline-black' : ''}`}
              />
            )}
            <div className="text-center">
              <h4 className="text-xs">{option.title}</h4>

            </div>
            </div>
          )
        })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

function ScrollControls({ fullHeight = false, paddingBottom = false }: { fullHeight?: boolean; paddingBottom?: boolean }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const host = ref.current?.parentElement
    if (!host) return
    const scroller = host.querySelector('.scroll-buttons') as HTMLElement | null
    if (!scroller) return
    // Ensure visible by default
    const leftBtnInit = host.querySelector<HTMLButtonElement>('[data-scroll-left]')
    const rightBtnInit = host.querySelector<HTMLButtonElement>('[data-scroll-right]')
    if (leftBtnInit) leftBtnInit.style.display = 'flex'
    if (rightBtnInit) rightBtnInit.style.display = 'flex'
    const update = () => {
      const atStart = scroller.scrollLeft <= 0
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1
      const leftBtn = host.querySelector<HTMLButtonElement>('[data-scroll-left]')
      const rightBtn = host.querySelector<HTMLButtonElement>('[data-scroll-right]')
      const canScroll = scroller.scrollWidth - scroller.clientWidth > 1
      if (leftBtn) leftBtn.style.display = canScroll && !atStart ? 'flex' : 'none'
      if (rightBtn) rightBtn.style.display = canScroll && !atEnd ? 'flex' : 'none'
    }
    const ro = new ResizeObserver(() => update())
    ro.observe(scroller)
    requestAnimationFrame(update)
    setTimeout(update, 100)
    setTimeout(update, 300)
    scroller.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      scroller.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      ro.disconnect()
    }
  }, [])

  const scrollByAmount = (dir: number) => {
    const host = ref.current?.parentElement
    if (!host) return
    const scroller = host.querySelector('.scroll-buttons') as HTMLElement | null
    if (!scroller) return
    const step = Math.max(160, Math.round(scroller.clientWidth * 0.5))
    scroller.scrollBy({ left: dir * step, behavior: 'smooth' })
  }

  return (
    <div ref={ref} className="pointer-events-none select-none">
      <button
        type="button"
        data-scroll-left
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollByAmount(-1) }}
        className={`pointer-events-auto absolute left-0 w-8 ${fullHeight ? 'inset-y-0' : 'bottom-0 h-36'} ${paddingBottom ? 'pb-8' : ''} flex items-center justify-center bg-[#ebe7e1] z-20 text-2xl`}
      >
        ‹
      </button>
      <button
        type="button"
        data-scroll-right
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollByAmount(1) }}
        className={`pointer-events-auto absolute right-0 w-8 ${fullHeight ? 'inset-y-0' : 'bottom-0 h-36'} ${paddingBottom ? 'pb-8' : ''} flex items-center justify-center bg-[#ebe7e1] z-20 text-2xl`}
      >
        ›
      </button>
    </div>
  )
}

function InlineSeamlessToggle() {
  const preferSeamless = useAppStore((s) => s.preferSeamless)
  const setPreferSeamless = useAppStore((s) => s.setPreferSeamless)
  const refreshCurrentModelForSelectedSize = useAppStore((s) => s.refreshCurrentModelForSelectedSize)
  const [local, setLocal] = useState(preferSeamless)
  const { t } = useTranslation()
  useEffect(() => setLocal(preferSeamless), [preferSeamless])
  const timerRef = useRef<number | null>(null)
  return (
    <div className="mb-4">
      <label className="w-fit flex gap-4 items-center justify-between cursor-pointer select-none">
        <div className="flex gap-2">
          <span className="text-sm font-bold text-gray-900">Seamless</span>
        </div>
        <button
          type="button"
          aria-pressed={local}
          onClick={() => {
            const next = !local
            setLocal(next)
            if (timerRef.current) window.clearTimeout(timerRef.current)
            timerRef.current = window.setTimeout(() => {
              setPreferSeamless(next)
              refreshCurrentModelForSelectedSize()
            }, 120)
          }}
          className={`inline-flex h-6 w-11 items-center rounded-full border transition-colors duration-200 ease-in-out px-[2px] ${local ? 'bg-brand border-brand justify-end' : 'bg-white border-brand justify-start'}`}
        >
          <span className={`inline-block h-5 w-5 rounded-full transition-colors duration-200 ease-in-out ${local ? 'bg-white' : 'bg-brand'}`} />
        </button>
      </label>
    </div>
  )
}

// Seamless toggle is now rendered in BedVisualizer above OptionsFetcher
